/**
 * Hybrid encryption for Family usage events (Phase 2).
 *
 * Protocol: X25519 ECIES-like. Each event uses a fresh ephemeral X25519
 * keypair on the member's client. ECDH with the family pubkey derives a
 * shared secret; HKDF expands it into an AES-256-GCM key bound to the
 * specific ephemeral↔family pair; AES-GCM encrypts the JSON-encoded
 * { itemName, siteUrl } payload. The ephemeral pubkey is transmitted
 * alongside the ciphertext so the admin's client can re-derive the
 * AES key from the family privkey.
 *
 * Domain separation: HKDF info label "usage-event-v1" is distinct from
 * the wrapping labels "vault-key-wrapping" and "account-key-wrapping".
 *
 * Forward secrecy at the event level: a stolen family privkey can still
 * decrypt past events (the ephemeral pubkey of each event is stored
 * alongside its ciphertext, so the same ECDH derivation runs). The
 * ephemeral keypair is wiped from memory after encryption; an attacker
 * who only steals an ephemeral privkey can't decrypt that one event.
 *
 * decryptUsageEventPayload takes the family pubkey as a third arg
 * because Web Crypto X25519 privkeys are imported non-extractable —
 * the decrypt path can't recover the pubkey from the privkey alone.
 * The pubkey is publicly known anyway (it's on Organization.publicKey),
 * so requiring callers to pass it is the cleaner protocol.
 */

const HKDF_INFO = 'usage-event-v1';

export interface EncryptedUsagePayload {
  encryptedPayload: string;      // base64
  encryptedPayloadIv: string;    // base64 (12 bytes)
  ephemeralPublicKey: string;    // base64url (32 bytes)
}

export interface UsageEventPlaintext {
  itemName: string;
  siteUrl: string;
}

export async function encryptUsageEventPayload(
  plaintext: UsageEventPlaintext,
  familyPublicKey: Uint8Array,
): Promise<EncryptedUsagePayload> {
  // 1. Generate ephemeral X25519 keypair
  const ephKeyPair = (await crypto.subtle.generateKey(
    { name: 'X25519' },
    true,
    ['deriveBits'],
  )) as CryptoKeyPair;

  const ephPubJwk = await crypto.subtle.exportKey('jwk', ephKeyPair.publicKey);
  const ephPubBytes = base64UrlToBytes(ephPubJwk.x!);

  // 2. Import family pubkey as a CryptoKey for ECDH
  const familyPubKey = await crypto.subtle.importKey(
    'jwk',
    { kty: 'OKP', crv: 'X25519', x: bytesToBase64Url(familyPublicKey) },
    { name: 'X25519' },
    true,
    [],
  );

  // 3. ECDH → 32-byte shared secret
  const sharedSecret = await crypto.subtle.deriveBits(
    { name: 'X25519', public: familyPubKey },
    ephKeyPair.privateKey,
    256,
  );

  // 4. HKDF: salt = eph_pub || family_pub, info = "usage-event-v1"
  const salt = new Uint8Array(64);
  salt.set(ephPubBytes, 0);
  salt.set(familyPublicKey, 32);

  const hkdfKeyMaterial = await crypto.subtle.importKey(
    'raw', sharedSecret, 'HKDF', false, ['deriveKey'],
  );
  const aesKey = await crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt, info: new TextEncoder().encode(HKDF_INFO) },
    hkdfKeyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt'],
  );

  // 5. AES-GCM encrypt JSON.stringify(plaintext)
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plainBytes = new TextEncoder().encode(JSON.stringify(plaintext));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, plainBytes as BufferSource),
  );

  return {
    encryptedPayload: bytesToBase64(ciphertext),
    encryptedPayloadIv: bytesToBase64(iv),
    ephemeralPublicKey: bytesToBase64Url(ephPubBytes),
  };
}

export async function decryptUsageEventPayload(
  ciphertext: EncryptedUsagePayload,
  familyPrivateKey: Uint8Array,
  familyPublicKey: Uint8Array,
): Promise<UsageEventPlaintext> {
  // Import family privkey for ECDH
  const familyPrivKey = await crypto.subtle.importKey(
    'jwk',
    {
      kty: 'OKP',
      crv: 'X25519',
      x: bytesToBase64Url(familyPublicKey),
      d: bytesToBase64Url(familyPrivateKey),
    } as JsonWebKey,
    { name: 'X25519' },
    false,
    ['deriveBits'],
  );

  // Import ephemeral pubkey
  const ephPubBytes = base64UrlToBytes(ciphertext.ephemeralPublicKey);
  const ephPubKey = await crypto.subtle.importKey(
    'jwk',
    { kty: 'OKP', crv: 'X25519', x: ciphertext.ephemeralPublicKey },
    { name: 'X25519' },
    true,
    [],
  );

  // ECDH → same shared secret
  const sharedSecret = await crypto.subtle.deriveBits(
    { name: 'X25519', public: ephPubKey },
    familyPrivKey,
    256,
  );

  // HKDF: same salt / info as encrypt
  const salt = new Uint8Array(64);
  salt.set(ephPubBytes, 0);
  salt.set(familyPublicKey, 32);

  const hkdfKeyMaterial = await crypto.subtle.importKey(
    'raw', sharedSecret, 'HKDF', false, ['deriveKey'],
  );
  const aesKey = await crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt, info: new TextEncoder().encode(HKDF_INFO) },
    hkdfKeyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt'],
  );

  // Decrypt
  const ivBytes = base64ToBytes(ciphertext.encryptedPayloadIv);
  const ctBytes = base64ToBytes(ciphertext.encryptedPayload);
  const plainBytes = new Uint8Array(
    await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivBytes.buffer as ArrayBuffer },
      aesKey,
      ctBytes.buffer as ArrayBuffer,
    ),
  );
  const plain = JSON.parse(new TextDecoder().decode(plainBytes));
  return { itemName: plain.itemName, siteUrl: plain.siteUrl ?? '' };
}

function bytesToBase64(b: Uint8Array): string {
  let s = '';
  for (const c of b) s += String.fromCharCode(c);
  return btoa(s);
}

function bytesToBase64Url(b: Uint8Array): string {
  return bytesToBase64(b).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64ToBytes(b64: string): Uint8Array {
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

function base64UrlToBytes(b64url: string): Uint8Array {
  const pad = b64url.length % 4 === 0 ? '' : '='.repeat(4 - (b64url.length % 4));
  return base64ToBytes((b64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
}
